import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PP_FORM_SERVICES } from "@/data/preservation";

const cls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-[#0B132B] focus:border-gold focus:outline-none";
const EMPTY = { company: "", contact_name: "", email: "", phone: "", service: "Securing", county: "", property_address: "", notes: "", website: "" };

const Field = ({ label, children }) => (
  <label className="block text-sm font-medium text-[#0B132B]">{label}<div className="mt-1.5">{children}</div></label>
);

export const VendorForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vendor-requests", f);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the required fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-gold/40 bg-white p-8" data-testid="vendor-form-success">
        <CheckCircle2 className="h-8 w-8 text-gold" />
        <h3 className="mt-3 font-display text-2xl font-bold text-[#0B132B]">Request received.</h3>
        <p className="mt-2 text-sm text-slate-600">We'll send our vendor packet or quote to {f.email} shortly. A confirmation is on its way to your inbox.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="vendor-form">
      <Field label="Company *"><input required minLength={2} className={cls} value={f.company} onChange={set("company")} data-testid="vendor-company" /></Field>
      <Field label="Contact name *"><input required minLength={2} className={cls} value={f.contact_name} onChange={set("contact_name")} data-testid="vendor-contact-name" /></Field>
      <Field label="Email *"><input required type="email" className={cls} value={f.email} onChange={set("email")} data-testid="vendor-email" /></Field>
      <Field label="Phone"><input type="tel" className={cls} value={f.phone} onChange={set("phone")} data-testid="vendor-phone" /></Field>
      <Field label="Service needed">
        <select className={cls} value={f.service} onChange={set("service")} data-testid="vendor-service">{PP_FORM_SERVICES.map((s) => <option key={s}>{s}</option>)}</select>
      </Field>
      <Field label="County"><input className={cls} value={f.county} onChange={set("county")} data-testid="vendor-county" /></Field>
      <div className="sm:col-span-2"><Field label="Property address"><input className={cls} value={f.property_address} onChange={set("property_address")} data-testid="vendor-address" /></Field></div>
      <div className="sm:col-span-2"><Field label="Notes"><textarea rows={4} className={cls} value={f.notes} onChange={set("notes")} data-testid="vendor-notes" /></Field></div>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <button type="submit" disabled={busy} data-testid="vendor-submit"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-[#E5C158] disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request Vendor Packet
      </button>
    </form>
  );
};
